import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { StorageData } from '~/Components/Storage';
import { DeviceModel, useSelector, useLogger } from '~Hooks';

type HapticFeedbackTypes =
  | 'selection'
  | 'impactLight'
  | 'impactMedium'
  | 'impactHeavy'
  | 'rigid'
  | 'soft'
  | 'notificationSuccess'
  | 'notificationWarning'
  | 'notificationError'
  | 'clockTick'
  | 'contextClick'
  | 'keyboardPress'
  | 'keyboardRelease'
  | 'keyboardTap'
  | 'longPress'
  | 'textHandleMove'
  | 'virtualKey'
  | 'virtualKeyRelease'
  | 'effectClick'
  | 'effectDoubleClick'
  | 'effectHeavyClick'
  | 'effectTick';

/**
 * This schema is defined in the order-manager-app repo,
 * inside the MessageHandler.ts file.
 * If you want more device info, make an update there and here
 * and then make sure to mark the new field as optional since not
 * all clients will run the latest native version.
 */
export interface DeviceInfo {
  device: string;
  deviceId: string;
  deviceType: string;
  display: string;
  deviceName: string;
  model: DeviceModel;
  serialNumber: string;
  brand: string;
  systemName: string;
  systemVersion: string;
  isTablet: boolean;
  hasNotch: boolean;
}

export type PrinterStatusResponse = {
  isResponsive: boolean;
  drawerIsOpen: boolean;
  coverIsOpen: boolean;
  isOffline: boolean;
  paperNearEmpty: boolean;
  paperEmpty: boolean;
};

export const useBridgeApi = () => {
  const reloadLogger = useLogger('mount');

  // Save interval IDs to prevent memory leaks
  const [intervalIds, setIntervalIds] = useState<number[]>([]);

  // Our hashmap of request IDs and data
  const requests = useRef<Record<string, any>>({});

  // Message handler
  function onMessage(message: any) {
    let data = message.data;

    try {
      data = JSON.parse(message.data);
    } catch (e) {} // eslint-disable-line

    // If this message contained a request ID, store the response
    if (data.requestId) {
      requests.current[data.requestId] = data.payload;
    }
  }

  function request(data: any) {
    window.postMessageBridge(data);
  }

  async function requestResponse<T>(data: any) {
    // Create a unique ID to use for this request
    const requestId = uuid();

    // Send the request to the app wrapper
    request({
      ...data,
      requestId,
    });

    // Return a new promise which continually checks for a response from the wrapper
    return new Promise<T>((resolve) => {
      const id = setInterval(() => {
        // If we got a response, resolve
        if (requests.current[requestId]) {
          resolve(requests.current[requestId]);
          clearInterval(id);

          // Remove the data from "storage" after we have resolved
          requests.current[requestId] = null;
        }
      }, 150);

      // Save this interval ID so we can remove it later if needed
      setIntervalIds((state) => [...state, id]);
    });
  }

  useEffect(() => {
    // Add the event listener for messages passed from app wrapper
    document.addEventListener('message', onMessage);
    window.addEventListener('message', onMessage);

    // To prevent memory leaks, remove all event listeners on demount
    return () => {
      document.removeEventListener('message', onMessage);
      window.removeEventListener('message', onMessage);

      // Clear all pending intervals
      intervalIds.forEach((id) => clearInterval(id));
    };
  }, []);

  return {
    reload: () => {
      const stackTrace = new Error().stack;

      reloadLogger.info('Location reload: useBridgeApi', {
        stackTrace,
      });

      if (!window.hasNativeWrapper && window.__DEV__) {
        setTimeout(() => {
          window.location.reload();
        }, 100);
        return null;
      }

      return requestResponse({
        type: 'RELOAD',
      });
    },

    vibrate: (feedbackType: HapticFeedbackTypes) => {
      return requestResponse({
        type: 'VIBRATE',
        payload: {
          feedbackType,
        },
      });
    },

    showZettleTipSettings: () => {
      return requestResponse({
        type: 'SHOW_ZETTLE_TIP_SETTINGS',
      });
    },

    printZettleDock: (base64String: string) => {
      return requestResponse({
        type: 'PRINT_ON_ZETTLE_DOCK',
        payload: {
          base64String,
        },
      });
    },

    getZReport: async (date: string) => {
      const data = await requestResponse({
        getZReport: date,
      });

      return data;
    },

    getZReportDays: async () => {
      const data = await requestResponse({ getZReportDays: true });

      return data;
    },

    getStorageData: () => {
      return requestResponse({ getStorageData: true }) as Promise<StorageData>;
    },

    updateStorage: async (data: StorageData) => {
      return requestResponse({ setStorageData: data });
    },

    adyenRequest: async (url: string, body: string, useProdCert: boolean) => {
      // Use local fetch if outside native wrapper
      if (!window.hasNativeWrapper && window.__DEV__) {
        // 1 min timeout on fetch for testing purposes
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);

        try {
          const res = await fetch(url, {
            signal: controller.signal,
            method: 'POST',
            body,
          });
          const data = await res.json();
          clearTimeout(timeoutId);

          return data as any;
        } catch (e) {
          return null;
        }
      }

      const data = await requestResponse({ adyenRequest: { url, body, useProdCert } });

      return data as any;
    },

    iZettleRefund: async (url: string, body: string, useProdCert: boolean) => {
      const data = await requestResponse({ iZettle: { url, body, useProdCert } });

      return data as any;
    },

    scanQRCode: async () => {
      const data = await requestResponse({ readQRCode: true });

      return data as string | null;
    },

    getDeviceInfo: async (): Promise<DeviceInfo> => {
      return requestResponse<DeviceInfo>({
        type: 'DEVICE_INFO',
      });
    },

    playSound() {
      try {
        window.webkit.messageHandlers.reactNative.postMessage({
          data: 'alert-sound',
        });
      } catch (e) {
        console.log('Message failed');
      }

      try {
        window.ReactNativeWebView.postMessage('alert-sound');
      } catch (e) {
        console.log('Message failed');
      }
    },

    kickOutDrawer() {
      return requestResponse({ type: 'KICK_OUT_DRAWER' });
    },

    getPrinterStatus(printerId: string) {
      return requestResponse({
        type: 'GET_PRINTER_STATUS',
        payload: {
          printerId,
        },
      }) as Promise<PrinterStatusResponse>;
    },

    fetch: async (
      url: string,
      options?: { method?: string; headers?: Record<string, string>; body?: string },
    ): Promise<{ status: number; headers: Record<string, string>; body: string } | { error: string }> => {
      return requestResponse({
        type: 'FETCH',
        payload: {
          url,
          method: options?.method || 'GET',
          headers: options?.headers,
          body: options?.body,
        },
      });
    },
  };
};
